import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Clock, Plus, Pencil, Trash2, Sun, Sunrise } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const EMPTY = { nome: "", ora_inizio: "09:00", ora_fine: "13:00", tipo_tariffa: "mezza_giornata", ordine: 0, attivo: true };
const TARIFFA_LABEL = { mezza_giornata: "Mezza giornata", giornata: "Giornata" };

export function FasceOrarie() {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("timeslots.manage");
  const [slots, setSlots] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const load = async () => { try { setSlots((await api.get("/timeslots")).data); } catch (e) {} };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, ordine: slots.length }); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...EMPTY, ...s }); setOpen(true); };

  const save = async () => {
    if (!form.nome.trim()) { toast.error("Nome fascia obbligatorio"); return; }
    if (!form.ora_inizio || !form.ora_fine || form.ora_fine <= form.ora_inizio) { toast.error("Orario di fine deve essere successivo all'inizio"); return; }
    const p = { ...form, ordine: parseInt(form.ordine) || 0 };
    try {
      if (editing) await api.put(`/timeslots/${editing.id}`, p);
      else await api.post("/timeslots", p);
      toast.success("Fascia salvata"); setOpen(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const del = async (s) => {
    try { await api.delete(`/timeslots/${s.id}`); toast.success("Fascia eliminata"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div data-testid="fasce-orarie-tab">
      <p className="text-sm text-zinc-500 mb-4">Definisci le fasce orarie prenotabili. La fascia determina la tariffa applicata (mezza giornata / giornata).</p>
      {canManage && (
        <button onClick={openNew} data-testid="add-slot-btn" className="mb-4 flex items-center gap-2 rounded-sm bg-[#f98125] text-white font-bold uppercase tracking-wide px-4 py-2.5 text-sm hover:bg-[#d96a12] transition-colors">
          <Plus className="h-4 w-4" /> Nuova fascia
        </button>
      )}
      {slots.length === 0 ? (
        <div className="text-zinc-600 text-sm py-12 text-center border border-dashed border-zinc-800 rounded-sm">Nessuna fascia oraria.</div>
      ) : (
        <div className="space-y-2">
          {slots.map((s) => (
            <div key={s.id} data-testid={`slot-row-${s.id}`} className="bg-[#141414] border border-zinc-800 rounded-sm p-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 bg-[#f98125]/15 rounded-sm flex items-center justify-center">
                  {s.tipo_tariffa === "giornata" ? <Sun className="h-5 w-5 text-[#f98125]" /> : <Sunrise className="h-5 w-5 text-[#f98125]" />}
                </div>
                <div>
                  <div className="font-semibold flex items-center gap-2">{s.nome}
                    {!s.attivo && <span className="text-[10px] uppercase tracking-wider text-zinc-600">non attiva</span>}
                  </div>
                  <div className="text-xs text-zinc-500 flex items-center gap-3 mt-0.5">
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {s.ora_inizio} – {s.ora_fine}</span>
                    <span className="bg-zinc-800/60 text-zinc-300 px-2 py-0.5 rounded-sm">{TARIFFA_LABEL[s.tipo_tariffa]}</span>
                  </div>
                </div>
              </div>
              {canManage && (
                <div className="flex items-center gap-1">
                  <button onClick={() => openEdit(s)} data-testid={`edit-slot-${s.id}`} className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-800 rounded-sm"><Pencil className="h-4 w-4" /></button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><button data-testid={`delete-slot-${s.id}`} className="p-1.5 text-zinc-500 hover:text-[#FF3B30] hover:bg-zinc-800 rounded-sm"><Trash2 className="h-4 w-4" /></button></AlertDialogTrigger>
                    <AlertDialogContent className="bg-[#141414] border-zinc-800">
                      <AlertDialogHeader><AlertDialogTitle>Eliminare la fascia "{s.nome}"?</AlertDialogTitle><AlertDialogDescription>Azione irreversibile.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel><AlertDialogAction onClick={() => del(s)} className="rounded-sm bg-[#FF3B30] hover:bg-[#cc2f26]">Elimina</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141414] border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2"><Clock className="h-5 w-5 text-[#f98125]" /> {editing ? "Modifica fascia" : "Nuova fascia"}</DialogTitle>
            <DialogDescription>Imposta nome, orari e tipo di tariffa della fascia.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Nome *</Label><Input data-testid="slot-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" placeholder="Es. Mattino" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Ora inizio *</Label><Input type="time" data-testid="slot-inizio" value={form.ora_inizio} onChange={(e) => setForm({ ...form, ora_inizio: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Ora fine *</Label><Input type="time" data-testid="slot-fine" value={form.ora_fine} onChange={(e) => setForm({ ...form, ora_fine: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" /></div>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Tipo tariffa *</Label>
              <Select value={form.tipo_tariffa} onValueChange={(v) => setForm({ ...form, tipo_tariffa: v })}>
                <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="slot-tipo"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-zinc-800">
                  <SelectItem value="mezza_giornata">Mezza giornata</SelectItem>
                  <SelectItem value="giornata">Giornata</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4 items-end">
              <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Ordine</Label><Input type="number" data-testid="slot-ordine" value={form.ordine} onChange={(e) => setForm({ ...form, ordine: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" /></div>
              <div className="flex items-center justify-between pb-2"><span className="text-sm text-zinc-300">Attiva</span><Switch checked={form.attivo} onCheckedChange={(v) => setForm({ ...form, attivo: v })} data-testid="slot-attiva" /></div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="rounded-sm border border-zinc-700 px-5 py-2.5 hover:bg-zinc-800 transition-colors">Annulla</button>
            <button onClick={save} data-testid="slot-save" className="rounded-sm bg-[#f98125] text-white font-bold px-5 py-2.5 hover:bg-[#d96a12] transition-colors">Salva</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
